import {
  AnalyticsEventName,
  AnalyticsPageType,
  ShopifySalesChannel,
  getClientBrowserParameters,
  sendShopifyAnalytics,
} from '@shopify/hydrogen-react';
import type { ShopifyAnalytics } from '@shopify/hydrogen-react';
import { shopifyConfig, shopifyAnalyticsConfig } from '../data/shopify';

const SHOP_GID_KEY = 'mfl_shopify_shop_gid';

/** True once the Headless storefront token (or a numeric Shop ID) is configured. */
export const shopifyAnalyticsEnabled = () =>
  /^\d+$/.test(shopifyAnalyticsConfig.shopId.trim()) || shopifyAnalyticsConfig.storefrontToken.trim().length > 0;

let shopGidPromise: Promise<string | null> | null = null;

/** Resolves the shop's global ID — from config, session cache, or one Storefront API call. */
export function resolveShopGid(): Promise<string | null> {
  const numeric = shopifyAnalyticsConfig.shopId.trim();
  if (/^\d+$/.test(numeric)) return Promise.resolve(`gid://shopify/Shop/${numeric}`);
  if (shopGidPromise) return shopGidPromise;

  try {
    const cached = sessionStorage.getItem(SHOP_GID_KEY);
    if (cached) return (shopGidPromise = Promise.resolve(cached));
  } catch {
    // sessionStorage unavailable — fall through to the API.
  }

  const { storefrontToken, storefrontApiVersion } = shopifyAnalyticsConfig;
  shopGidPromise = fetch(`https://${shopifyConfig.storeDomain}/api/${storefrontApiVersion}/graphql.json`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': storefrontToken.trim(),
    },
    body: JSON.stringify({ query: '{ shop { id } }' }),
  })
    .then((r) => (r.ok ? r.json() : null))
    .then((json: { data?: { shop?: { id?: string } } } | null) => {
      const id = json?.data?.shop?.id ?? null;
      if (id) {
        try {
          sessionStorage.setItem(SHOP_GID_KEY, id);
        } catch {
          // ignore
        }
      } else {
        shopGidPromise = null; // allow a retry on the next page view
      }
      return id;
    })
    .catch(() => {
      shopGidPromise = null;
      return null;
    });
  return shopGidPromise;
}

/**
 * Shared payload for every funnel event: browser/visitor tokens, Headless sales channel and
 * (when configured) the Headless storefront ID, so Shopify attributes events to this funnel.
 */
export function basePayload(shopId: string, pageType: string) {
  const storefrontId = shopifyAnalyticsConfig.storefrontId.trim();
  return {
    ...getClientBrowserParameters(),
    shopId,
    currency: 'USD' as const,
    acceptedLanguage: 'EN' as const,
    hasUserConsent: true,
    shopifySalesChannel: ShopifySalesChannel.headless,
    ...(storefrontId ? { storefrontId } : {}),
    pageType,
  };
}

/**
 * Sends to Shopify's analytics endpoint directly (monorail-edge), which accepts requests from
 * m1.marineforceled.com. Posting to the store domain from another subdomain can be dropped.
 */
export function sendToShopify(event: ShopifyAnalytics) {
  return sendShopifyAnalytics(event);
}

const pageTypeFor = (path: string) => {
  if (path === '/') return AnalyticsPageType.home;
  if (path === '/m1-pro') return AnalyticsPageType.product;
  if (path === '/systems' || path === '/shop' || path === '/build') return AnalyticsPageType.collection;
  if (path === '/cart' || path === '/checkout') return AnalyticsPageType.cart;
  return AnalyticsPageType.page;
};

/**
 * Sends a page view to Shopify Analytics (Live View, Sessions, Online store conversion),
 * so funnel visitors on m1.marineforceled.com appear in Shopify like storefront visitors.
 * Waits one tick so the shared visitor cookies exist before the first event.
 * Fully independent of the Meta Pixel.
 */
export function sendShopifyPageView(path: string) {
  if (!shopifyAnalyticsEnabled() || typeof window === 'undefined') return;
  window.setTimeout(() => {
    resolveShopGid()
      .then((shopId) => {
        if (!shopId) return;
        return sendToShopify({
          eventName: AnalyticsEventName.PAGE_VIEW,
          payload: basePayload(shopId, pageTypeFor(path)),
        });
      })
      .catch(() => {
        // Analytics must never break the storefront.
      });
  }, 300);
}
